import {
  BarChart3,
  Building2,
  FolderTree,
  ClipboardList,
  Gauge,
  KeyRound,
  LayoutGrid,
  LayoutTemplate,
  Megaphone,
  Package,
  Scale,
  Settings,
  ShieldCheck,
  Star,
  Store,
  Tags,
  User,
  UserCog,
  Users,
  Wallet,
  Globe,
  CreditCard,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/features/admin/api/dashboardApi';
import { TrialBanner } from '@/features/subscription/components/TrialBanner';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ROLES } from '@/features/auth/roles';
import { DashLayout } from '@/ui/dash/DashLayout';
import { MobileBottomNav } from '@/ui/MobileBottomNav';

/**
 * The three signed-in shells.
 *
 * Navigation is data, and each item that has no backend yet carries
 * `disabled` with the phase that will bring it. Rendering those as dead links
 * would be worse than admitting the gap - the wireframe promises a full
 * seller console, and this says plainly which parts of it are real.
 */

export function AdminLayout() {
  const { hasRole, user } = useAuth();
  // Only a platform operator sees the shop list, and the shell must agree
  // with the route guard - a nav item that renders a 403 is worse than none.
  const isOperator = hasRole(ROLES.SUPER_ADMIN);
  // An expired shop can open only its subscription page until it renews.
  const locked = !isOperator && Boolean(user?.subscription_expired);

  // The shop admin's queue counts, shown as badges. Same query key as the
  // overview page, so the two share one request and one cache entry.
  const { data: queue } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: dashboardApi.get,
    enabled: !isOperator && !locked,
    staleTime: 15_000,
  });

  const allNavItems = isOperator
    ? [
        // The operator manages the platform - its shops, the people who run
        // them, shared categories and keys - not any one shop's daily work.
        {
          group: 'Platform',
          to: '/admin/overview',
          label: 'Overview',
          short: 'Home',
          tab: true,
          icon: Gauge,
        },
        { group: 'Platform', to: '/admin/customers', label: 'Shops', tab: true, icon: Building2 },
        { group: 'Platform', to: '/admin/subscriptions', label: 'Subscriptions', icon: CreditCard },
        { group: 'Platform', to: '/admin/categories', label: 'Categories', icon: FolderTree },
        { group: 'Platform', to: '/admin/store', label: 'Store page', icon: LayoutTemplate },
        {
          group: 'Access',
          to: '/admin/api-keys',
          label: 'API keys',
          short: 'Keys',
          tab: true,
          icon: KeyRound,
        },
        {
          group: 'Access',
          to: '/admin/admins',
          label: 'Shop admins',
          short: 'Admins',
          tab: true,
          icon: UserCog,
        },
        {
          group: 'Coming later',
          label: 'Reports',
          icon: BarChart3,
          disabled: true,
          note: 'Phase 7',
        },
        {
          group: 'Coming later',
          label: 'Settings',
          icon: Settings,
          disabled: true,
          note: 'Phase 7',
        },
      ]
    : [
        {
          group: 'Daily work',
          to: '/admin',
          label: 'Overview',
          short: 'Home',
          tab: true,
          end: true,
          icon: Gauge,
        },
        {
          group: 'Daily work',
          to: '/admin/orders',
          label: 'Orders',
          tab: true,
          icon: Package,
          badge: queue?.orders?.awaiting_approval,
        },
        {
          group: 'Daily work',
          to: '/admin/vendors',
          label: 'Vendors',
          tab: true,
          icon: Store,
          badge: queue?.vendors?.awaiting_review,
        },
        {
          group: 'Daily work',
          to: '/admin/catalog',
          label: 'Products',
          tab: true,
          icon: ShieldCheck,
          badge: queue?.catalog?.awaiting_moderation,
        },
        { group: 'Grow', to: '/admin/seasons', label: 'Seasonal sales', icon: Megaphone },
        { group: 'Grow', to: '/admin/website', label: 'Website', icon: Globe },
        { group: 'Grow', to: '/admin/subscription', label: 'My Subscription', icon: CreditCard },
        { group: 'Team', to: '/admin/users', label: 'People', icon: Users },
        { group: 'Coming later', label: 'Payouts', icon: Wallet, disabled: true, note: 'Phase 6' },
        { group: 'Coming later', label: 'Returns', icon: Scale, disabled: true, note: 'Phase 4' },
        {
          group: 'Coming later',
          label: 'Reports',
          icon: BarChart3,
          disabled: true,
          note: 'Phase 7',
        },
      ];

  const navItems = locked
    ? allNavItems.map((item) =>
        item.disabled || item.to === '/admin/subscription'
          ? item
          : {
              ...item,
              disabled: true,
              badge: undefined,
              note: 'Renew your subscription to use this',
              disabledTag: 'Locked',
            },
      )
    : allNavItems;

  return (
    <DashLayout
      title={isOperator ? 'Platform owner' : 'Shop admin'}
      navItems={navItems}
      banner={!isOperator ? <TrialBanner /> : null}
    />
  );
}

export function VendorLayout() {
  return (
    <DashLayout
      title="Seller"
      navItems={[
        {
          group: 'My shop',
          to: '/vendor',
          label: 'Overview',
          short: 'Home',
          tab: true,
          end: true,
          icon: Gauge,
        },
        {
          group: 'My shop',
          to: '/vendor/products',
          label: 'My products',
          short: 'Products',
          tab: true,
          icon: Tags,
        },
        {
          group: 'My shop',
          to: '/vendor/orders',
          label: 'Orders to pack',
          short: 'Orders',
          tab: true,
          icon: Package,
        },
        {
          group: 'My shop',
          to: '/vendor/onboarding',
          label: 'My details',
          short: 'Details',
          tab: true,
          icon: ClipboardList,
        },
        { group: 'Coming later', label: 'Payouts', icon: Wallet, disabled: true, note: 'Phase 6' },
        {
          group: 'Coming later',
          label: 'Promotions',
          icon: Megaphone,
          disabled: true,
          note: 'Phase 4',
        },
        { group: 'Coming later', label: 'Reviews', icon: Star, disabled: true, note: 'Phase 4' },
        {
          group: 'Coming later',
          label: 'Analytics',
          icon: BarChart3,
          disabled: true,
          note: 'Phase 7',
        },
      ]}
    />
  );
}

export function AccountLayout() {
  return (
    <div className="pb-16 md:pb-0">
      <DashLayout
        title="My account"
        hideMobileNav
        navItems={[
          { to: '/account', label: 'Overview', end: true, icon: User },
          { to: '/account/orders', label: 'My orders', icon: Package },
          { to: '/', label: 'Continue shopping', icon: LayoutGrid },
        ]}
      />
      <MobileBottomNav />
    </div>
  );
}
